import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export default function WhatsAppSender() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success?: boolean; error?: string; messageId?: string } | null>(null);
  const [messageType, setMessageType] = useState<'sms' | 'whatsapp'>('sms');

  const sendMessage = async () => {
    if (!phoneNumber || !message) {
      setResult({ success: false, error: 'Please enter both phone number and message' });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const endpoint = messageType === 'sms' 
        ? 'https://biig-api.b-9f2.workers.dev/api/whatsapp/sms'
        : 'https://biig-api.b-9f2.workers.dev/api/whatsapp/send';
        
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: phoneNumber,
          message: message
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setResult({ success: true, messageId: data.messageId });
        // Clear form on success
        setPhoneNumber('');
        setMessage('');
      } else {
        setResult({ success: false, error: data.error || 'Failed to send message' });
      }
    } catch (error) {
      setResult({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Network error' 
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 p-8">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold text-green-600 mb-2">Message Sender</h1>
          <p className="text-gray-600 mb-4">Send REAL SMS from (628) 888-6038</p>
          
          {/* Message Type Selector */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setMessageType('sms')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                messageType === 'sms' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              SMS (Working!)
            </button>
            <button
              onClick={() => setMessageType('whatsapp')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                messageType === 'whatsapp' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              WhatsApp
            </button>
          </div>

          <div className="space-y-6">
            {/* Phone Number Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {messageType === 'sms' ? 'Phone Number' : 'WhatsApp Number'}
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={messageType === 'sms' ? "+15555551234" : "+5511999887766"}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
              />
              <p className="text-xs text-gray-500 mt-1">
                {messageType === 'sms' 
                  ? 'Include country code (e.g., +1 for USA)' 
                  : 'Include country code (e.g., +55 for Brazil)'}
              </p>
            </div>

            {/* Message Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hello! This is a test message from the Pousada system."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{message.length} characters</p>
            </div>

            {/* Send Button */}
            <button
              onClick={sendMessage}
              disabled={sending || !phoneNumber || !message}
              className={`w-full py-4 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition-all ${
                sending || !phoneNumber || !message
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 active:scale-95'
              }`}
            >
              {sending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Send {messageType === 'sms' ? 'SMS' : 'WhatsApp'} Message
                </>
              )}
            </button>

            {/* Result Display */}
            {result && (
              <div className={`p-4 rounded-lg ${
                result.success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {result.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${
                      result.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {result.success ? 'Message Sent Successfully!' : 'Failed to Send'}
                    </p>
                    {result.messageId && (
                      <p className="text-xs text-green-600 mt-1">
                        Message ID: {result.messageId}
                      </p>
                    )}
                    {result.error && (
                      <p className="text-sm text-red-600 mt-1">
                        {result.error}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Setup Instructions */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">To make this work:</h3>
            <ol className="text-sm text-gray-600 space-y-2">
              <li>1. Sign up for a free Twilio account at twilio.com</li>
              <li>2. Get your Account SID and Auth Token</li>
              <li>3. Join the Twilio Sandbox for WhatsApp</li>
              <li>4. Add these to your Cloudflare Worker secrets:
                <ul className="ml-4 mt-1 text-xs">
                  <li>• TWILIO_ACCOUNT_SID</li>
                  <li>• TWILIO_AUTH_TOKEN</li>
                  <li>• TWILIO_WHATSAPP_NUMBER</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}